import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, MenuItem, Select, FormControl,
  Skeleton, IconButton, Button, Divider, Avatar, Chip, Tooltip,
  CircularProgress, ToggleButton, ToggleButtonGroup, useTheme, useMediaQuery,
} from '@mui/material';
import {
  ArrowLeft, Send, Lock, Unlock, Paperclip, AlertCircle, User,
  Building2, Clock, Monitor, Calendar, MessageSquare, Tag, CheckCircle2,
  ChevronDown,
} from 'lucide-react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTickets } from '../../hooks/useTickets';
import { useAdminAuthStore } from '../../store/authStore';
import { StatusBadge } from '../../components/StatusBadge';
import type { TicketStatus, TicketPriority } from '../../types/ticket';
import { axiosInstance } from '../../api/axios';

dayjs.extend(relativeTime);

const STATUSES: TicketStatus[] = [
  'Open', 'InProgress', 'WaitingForCustomer', 'WaitingForThirdParty', 'Resolved', 'Closed', 'Reopened',
];

const CATEGORY_LABELS: Record<string, string> = {
  BugIssue: 'Bug / Issue',
  BillingSubscription: 'Billing & Subscription',
  FeatureRequest: 'Feature Request',
  GeneralSupport: 'General Support',
};

const PRIORITY_CONFIG: Record<TicketPriority, { dot: string; color: string; bg: string; border: string }> = {
  Critical: { dot: '#EF4444', color: '#F87171', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.25)' },
  High:     { dot: '#F97316', color: '#FB923C', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.25)' },
  Normal:   { dot: '#3B82F6', color: '#60A5FA', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)' },
  Low:      { dot: '#64748B', color: '#94A3B8', bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.2)' },
};

function PriorityBadge({ priority }: { priority: TicketPriority }) {
  const cfg = PRIORITY_CONFIG[priority] ?? PRIORITY_CONFIG.Low;
  return (
    <Chip
      label={priority}
      size="small"
      sx={{
        height: 24, fontSize: '0.7rem', fontWeight: 700,
        bgcolor: cfg.bg, color: cfg.color,
        border: `1px solid ${cfg.border}`, borderRadius: '6px',
        '& .MuiChip-label': { px: 0.875 },
      }}
    />
  );
}

// ── Attachment chip ──────────────────────────────────────────────────────────
type AttachmentEntry = { path: string; name: string } | string;

function AttachmentChip({ blobPath, originalName }: { blobPath: string; originalName?: string }) {
  const [loading, setLoading] = useState(false);
  const fileName = originalName ?? blobPath.split('/').pop() ?? blobPath;
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  const isPdf = ext === 'pdf';

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get<{ data: string }>(
        '/v1/product-admin/support-tickets/attachment-url',
        { params: { blobPath, fileName: originalName } },
      );
      if (res.data.data) window.open(res.data.data, '_blank');
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  return (
    <Chip
      icon={loading ? <CircularProgress size={10} sx={{ ml: 0.5 }} /> : <Paperclip size={10} />}
      label={fileName}
      size="small"
      onClick={handleClick}
      clickable={!loading}
      sx={{
        height: 24, fontSize: '0.68rem', fontWeight: 500,
        bgcolor: isImage ? 'rgba(16,185,129,0.08)' : isPdf ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.05)',
        color: isImage ? '#6EE7B7' : isPdf ? '#FCA5A5' : '#94A3B8',
        border: '1px solid',
        borderColor: isImage ? 'rgba(16,185,129,0.2)' : isPdf ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)',
        cursor: loading ? 'wait' : 'pointer',
        '& .MuiChip-label': { px: 0.75 },
        '& .MuiChip-icon': { ml: 0.5 },
        '&:hover': { opacity: 0.8 },
        transition: 'opacity 0.15s',
      }}
    />
  );
}

function AttachmentList({ json }: { json?: string }) {
  if (!json) return null;
  let entries: AttachmentEntry[] = [];
  try { entries = JSON.parse(json); } catch { return null; }
  if (!entries.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
      {entries.map((e) => {
        const blobPath = typeof e === 'string' ? e : e.path;
        const originalName = typeof e === 'string' ? undefined : e.name;
        return <AttachmentChip key={blobPath} blobPath={blobPath} originalName={originalName} />;
      })}
    </Box>
  );
}

// ── Metadata row ─────────────────────────────────────────────────────────────
function MetaRow({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <Box sx={{ color: 'text.disabled', pt: 0.1, flexShrink: 0 }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: 'text.disabled', width: 80, flexShrink: 0, pt: 0.1, fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.72rem', wordBreak: 'break-word', flex: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { session } = useAdminAuthStore();
  const { useDetail, useAssign, useUpdateStatus, useAddComment } = useTickets();

  const ticketId = Number(id);
  const { data: ticket, isLoading } = useDetail(ticketId);
  const assignMutation = useAssign();
  const statusMutation = useUpdateStatus();
  const commentMutation = useAddComment();

  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<{ name: string; path: string }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const handleAssignToMe = () => {
    if (!session?.user) return;
    assignMutation.mutate({ id: ticketId, assignedTo: `${session.user.firstName} ${session.user.lastName}` });
  };

  const handleSendComment = () => {
    if (!newComment.trim()) return;
    const attachmentsJson = attachedFiles.length
      ? JSON.stringify(attachedFiles.map(f => ({ path: f.path, name: f.name })))
      : undefined;
    commentMutation.mutate(
      { id: ticketId, comment: newComment.trim(), isInternal, attachmentsJson },
      {
        onSuccess: () => {
          setNewComment('');
          setAttachedFiles([]);
          setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        },
      },
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setIsUploading(true);
    try {
      const res = await axiosInstance.post<{ data: { uploadUrl: string; blobPath: string } }>(
        '/v1/Institutes/upload-url',
        { moduleName: 'support', contentType: file.type || 'application/octet-stream' },
      );
      const { uploadUrl, blobPath } = res.data.data;
      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      setAttachedFiles(prev => [...prev, { name: file.name, path: blobPath }]);
    } catch { /* ignore */ } finally { setIsUploading(false); }
  };

  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 0, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="rounded" width={180} height={20} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={i === 0 ? 100 : 60} sx={{ borderRadius: '10px' }} />)}
          </Box>
          <Box sx={{ width: { md: 280 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={50} sx={{ borderRadius: '10px' }} />)}
          </Box>
        </Box>
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" sx={{ color: 'text.disabled', mb: 1 }}>Ticket not found</Typography>
        <Button onClick={() => navigate('/support-tickets')} startIcon={<ArrowLeft size={14} />}>
          Back to tickets
        </Button>
      </Box>
    );
  }

  const breached = ticket.slaBreach && ticket.status !== 'Resolved' && ticket.status !== 'Closed';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>

      {/* ── Back nav + ticket number ──────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <IconButton
          size="small"
          onClick={() => navigate('/support-tickets')}
          sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
        >
          <ArrowLeft size={15} />
        </IconButton>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
          Support Tickets
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>/</Typography>
        <Typography variant="caption" sx={{ color: 'primary.light', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem' }}>
          {ticket.ticketNumber}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* Status changer in header on mobile */}
        {isMobile && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={ticket.status}
              onChange={(e) => statusMutation.mutate({ id: ticketId, status: e.target.value })}
              sx={{ fontSize: '0.75rem', height: 30 }}
            >
              {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* ── Two-column layout ────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>

        {/* ── LEFT: main content ──────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Ticket header card */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(138,112,214,0.04) 0%, transparent 60%)' }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
              <PriorityBadge priority={ticket.priority as TicketPriority} />
              <StatusBadge status={ticket.status as TicketStatus} />
              <Chip
                label={CATEGORY_LABELS[ticket.category] ?? ticket.category}
                size="small"
                sx={{ height: 24, fontSize: '0.69rem', fontWeight: 600, bgcolor: 'rgba(255,255,255,0.05)', color: 'text.secondary', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '6px', '& .MuiChip-label': { px: 0.875 } }}
              />
              {ticket.module && (
                <Chip
                  icon={<Tag size={9} />}
                  label={ticket.module}
                  size="small"
                  sx={{ height: 24, fontSize: '0.69rem', fontWeight: 600, bgcolor: 'rgba(59,130,246,0.08)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '6px', '& .MuiChip-label': { px: 0.75 }, '& .MuiChip-icon': { ml: 0.5, color: '#3B82F6' } }}
                />
              )}
              {breached && (
                <Chip
                  icon={<AlertCircle size={10} />}
                  label="SLA Breached"
                  size="small"
                  sx={{ height: 24, fontSize: '0.69rem', fontWeight: 700, bgcolor: 'rgba(239,68,68,0.1)', color: '#F87171', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '6px', '& .MuiChip-label': { px: 0.75 }, '& .MuiChip-icon': { ml: 0.5, color: '#EF4444' } }}
                />
              )}
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              {ticket.subject}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <User size={12} style={{ color: '#64748B' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.73rem' }}>
                  {ticket.reportedByName ?? '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Building2 size={12} style={{ color: '#64748B' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.73rem' }}>
                  {ticket.instituteName ?? '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Calendar size={12} style={{ color: '#64748B' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.73rem' }}>
                  {dayjs(ticket.createdOn).format('DD MMM YYYY, HH:mm')}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Description */}
          {ticket.description && (
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px' }}>
              <Typography variant="overline" sx={{ color: 'text.disabled', display: 'block', mb: 1.25, fontSize: '0.65rem' }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.85, fontSize: '0.83rem', whiteSpace: 'pre-wrap' }}>
                {ticket.description}
              </Typography>
              <AttachmentList json={ticket.attachmentsJson} />
            </Paper>
          )}

          {/* Conversation */}
          <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden' }}>
            {/* Thread header */}
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.01)' }}>
              <MessageSquare size={14} style={{ color: '#64748B' }} />
              <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                Conversation
              </Typography>
              <Box sx={{ ml: 'auto', minWidth: 22, height: 22, borderRadius: '11px', bgcolor: 'rgba(138,112,214,0.15)', color: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700 }}>
                {ticket.comments.length}
              </Box>
            </Box>

            {/* Messages */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {ticket.comments.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                    <MessageSquare size={20} style={{ color: '#334155' }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>No messages yet</Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', opacity: 0.6 }}>Send the first reply below</Typography>
                </Box>
              )}

              {ticket.comments.map((c) => (
                <Box
                  key={c.id}
                  sx={{
                    p: 2, borderRadius: '10px',
                    bgcolor: c.isInternal
                      ? 'rgba(138,112,214,0.07)'
                      : c.isFromSupport
                      ? 'rgba(59,130,246,0.05)'
                      : 'rgba(255,255,255,0.025)',
                    border: '1px solid',
                    borderColor: c.isInternal
                      ? 'rgba(138,112,214,0.18)'
                      : c.isFromSupport
                      ? 'rgba(59,130,246,0.1)'
                      : 'rgba(255,255,255,0.05)',
                  }}
                >
                  {/* Comment header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar
                      sx={{
                        width: 26, height: 26, fontSize: '0.62rem', fontWeight: 700, flexShrink: 0,
                        bgcolor: c.isFromSupport ? 'rgba(138,112,214,0.25)' : 'rgba(100,116,139,0.2)',
                        color: c.isFromSupport ? '#A593E0' : '#94A3B8',
                      }}
                    >
                      {c.commentByName?.[0]?.toUpperCase() ?? (c.isFromSupport ? 'S' : 'U')}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                          {c.commentByName ?? (c.isFromSupport ? 'Support' : 'User')}
                        </Typography>
                        {c.isFromSupport && !c.isInternal && (
                          <Chip label="Support" size="small" sx={{ height: 16, fontSize: '0.57rem', fontWeight: 700, bgcolor: 'rgba(59,130,246,0.15)', color: '#60A5FA', '& .MuiChip-label': { px: 0.5 } }} />
                        )}
                        {c.isInternal && (
                          <Chip
                            icon={<Lock size={8} />}
                            label="Internal note"
                            size="small"
                            sx={{ height: 16, fontSize: '0.57rem', fontWeight: 700, bgcolor: 'rgba(138,112,214,0.2)', color: '#A593E0', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-icon': { ml: 0.5, color: '#8A70D6' } }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.67rem', flexShrink: 0 }}>
                      {dayjs(c.createdOn).format('DD MMM, HH:mm')}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>
                    {c.comment}
                  </Typography>
                  <AttachmentList json={c.attachmentsJson} />
                </Box>
              ))}
              <div ref={commentsEndRef} />
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />

            {/* ── Reply composer ── */}
            <Box
              sx={{
                p: 2,
                bgcolor: isInternal ? 'rgba(138,112,214,0.04)' : 'transparent',
                transition: 'background 0.2s',
              }}
            >
              {/* Mode toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <ToggleButtonGroup
                  exclusive
                  value={isInternal ? 'internal' : 'public'}
                  onChange={(_, v) => { if (v !== null) setIsInternal(v === 'internal'); }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      height: 28, fontSize: '0.7rem', fontWeight: 600, px: 1.25, py: 0,
                      border: '1px solid rgba(255,255,255,0.09)', gap: 0.5,
                      '&.Mui-selected[value="public"]': { bgcolor: 'rgba(16,185,129,0.12)', color: '#34D399', borderColor: 'rgba(16,185,129,0.25)' },
                      '&.Mui-selected[value="internal"]': { bgcolor: 'rgba(138,112,214,0.15)', color: '#A593E0', borderColor: 'rgba(138,112,214,0.3)' },
                    },
                  }}
                >
                  <ToggleButton value="public" sx={{ color: 'success.main' }}>
                    <Unlock size={12} /> Public Reply
                  </ToggleButton>
                  <ToggleButton value="internal" sx={{ color: '#A593E0' }}>
                    <Lock size={12} /> Internal Note
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                  {isInternal ? 'Only visible to the support team' : 'Will be sent to the user'}
                </Typography>
              </Box>

              {/* Attached files */}
              {attachedFiles.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {attachedFiles.map(f => (
                    <Chip
                      key={f.path}
                      label={f.name}
                      size="small"
                      onDelete={() => setAttachedFiles(prev => prev.filter(x => x.path !== f.path))}
                      sx={{ height: 24, fontSize: '0.68rem', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                  ))}
                </Box>
              )}

              {/* Textarea + send */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <Box sx={{ flex: 1, position: 'relative' }}>
                  <TextField
                    fullWidth multiline minRows={2} maxRows={8} size="small"
                    placeholder={isInternal ? 'Write an internal note…' : 'Write a reply to the user…'}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleSendComment(); }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.83rem', borderRadius: '10px',
                        bgcolor: isInternal ? 'rgba(138,112,214,0.05)' : 'rgba(255,255,255,0.03)',
                        pr: '44px',
                        '& fieldset': { borderColor: isInternal ? 'rgba(138,112,214,0.25)' : 'rgba(255,255,255,0.08)' },
                        '&:hover fieldset': { borderColor: isInternal ? 'rgba(138,112,214,0.4)' : 'rgba(255,255,255,0.14)' },
                        '&.Mui-focused fieldset': { borderColor: isInternal ? '#8A70D6' : '#10B981', borderWidth: 1.5 },
                      },
                    }}
                  />
                  <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx,.xlsx,.csv,.txt" />
                  <Tooltip title="Attach file">
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      sx={{ position: 'absolute', right: 8, bottom: 8, width: 28, height: 28, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
                    >
                      {isUploading ? <CircularProgress size={12} /> : <AttachFileIcon sx={{ fontSize: 15 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>

                <Tooltip title="Send (Ctrl+Enter)">
                  <span>
                    <IconButton
                      onClick={handleSendComment}
                      disabled={!newComment.trim() || commentMutation.isPending}
                      sx={{
                        width: 42, height: 42, borderRadius: '10px', flexShrink: 0,
                        bgcolor: isInternal ? 'rgba(138,112,214,0.18)' : 'rgba(16,185,129,0.13)',
                        color: isInternal ? '#A593E0' : 'success.main',
                        border: '1px solid',
                        borderColor: isInternal ? 'rgba(138,112,214,0.3)' : 'rgba(16,185,129,0.25)',
                        '&:hover': { bgcolor: isInternal ? 'rgba(138,112,214,0.28)' : 'rgba(16,185,129,0.22)' },
                        '&:disabled': { opacity: 0.3 },
                        transition: 'all 0.15s',
                      }}
                    >
                      {commentMutation.isPending ? <CircularProgress size={15} /> : <Send size={16} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.62rem', mt: 0.75, display: 'block' }}>
                Ctrl + Enter to send
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* ── RIGHT: metadata sidebar ─────────────────────────── */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* Actions card */}
          <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px' }}>
            <Typography variant="overline" sx={{ color: 'text.disabled', display: 'block', mb: 1.25, fontSize: '0.63rem' }}>
              Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <Typography variant="caption" sx={{ color: 'text.disabled', mb: 0.5, fontSize: '0.67rem' }}>Status</Typography>
                <Select
                  value={ticket.status}
                  onChange={(e) => statusMutation.mutate({ id: ticketId, status: e.target.value })}
                  disabled={statusMutation.isPending}
                  IconComponent={ChevronDown}
                  sx={{ fontSize: '0.78rem' }}
                >
                  {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem' }}>{s}</MenuItem>)}
                </Select>
              </FormControl>

              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleAssignToMe}
                disabled={assignMutation.isPending}
                startIcon={assignMutation.isPending ? <CircularProgress size={12} /> : <User size={13} />}
                sx={{ fontSize: '0.75rem', height: 34 }}
              >
                Assign to Me
              </Button>
            </Box>
          </Paper>

          {/* Info card */}
          <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px' }}>
            <Typography variant="overline" sx={{ color: 'text.disabled', display: 'block', mb: 0.5, fontSize: '0.63rem' }}>
              Details
            </Typography>
            <Box>
              <MetaRow icon={<User size={12} />} label="Reporter" value={ticket.reportedByName} />
              <MetaRow icon={<Building2 size={12} />} label="Institute" value={ticket.instituteName} />
              <MetaRow icon={<Tag size={12} />} label="Email" value={ticket.reportedByEmail} />
              <MetaRow icon={<Tag size={12} />} label="Phone" value={ticket.reportedByPhone} />
              <MetaRow icon={<User size={12} />} label="Assigned to" value={ticket.assignedTo ?? 'Unassigned'} />
              <MetaRow icon={<Calendar size={12} />} label="Created" value={dayjs(ticket.createdOn).format('DD MMM YYYY, HH:mm')} />
              {ticket.slaDeadline && (
                <MetaRow
                  icon={<Clock size={12} />}
                  label="SLA due"
                  value={`${dayjs(ticket.slaDeadline).format('DD MMM, HH:mm')}${breached ? ' ⚠' : ''}`}
                />
              )}
              {ticket.resolvedAt && (
                <MetaRow icon={<CheckCircle2 size={12} />} label="Resolved" value={dayjs(ticket.resolvedAt).format('DD MMM YYYY, HH:mm')} />
              )}
              {ticket.autoCloseScheduledAt && (
                <MetaRow icon={<Clock size={12} />} label="Auto-close" value={dayjs(ticket.autoCloseScheduledAt).format('DD MMM, HH:mm')} />
              )}
              {ticket.deviceInfo && (
                <MetaRow icon={<Monitor size={12} />} label="Device" value={ticket.deviceInfo} />
              )}
            </Box>
          </Paper>

          {/* Satisfaction (if rated) */}
          {ticket.satisfactionRating != null && (
            <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px' }}>
              <Typography variant="overline" sx={{ color: 'text.disabled', display: 'block', mb: 1, fontSize: '0.63rem' }}>
                Satisfaction
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.4, mb: 0.75 }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <Box key={n} sx={{ width: 24, height: 24, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', bgcolor: n <= ticket.satisfactionRating! ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.03)', border: '1px solid', borderColor: n <= ticket.satisfactionRating! ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.06)' }}>
                    {n <= ticket.satisfactionRating! ? '★' : '☆'}
                  </Box>
                ))}
              </Box>
              {ticket.satisfactionFeedback && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', fontStyle: 'italic' }}>
                  "{ticket.satisfactionFeedback}"
                </Typography>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}
